import type {Doc, Editor, LineHandle} from 'codemirror'
import CodeMirror from '../codemirror'
import {fetchPoll} from '@github-ui/fetch-utils'
import {getAsyncCodeEditor} from '../code-editor'
import {observe} from '@github-ui/selector-observer'
import {on} from 'delegated-events'
import {validate} from '../behaviors/html-validation'
import verifySsoSession from '../sso'
import {getBaseFetchHeaders} from '@github-ui/fetch-headers'
import {forceAnnouncementToScreenReaders} from '@github-ui/code-view/utilities/lines'

interface FileMergeData {
  document: Doc
  headDocument: Doc
  baseDocument: Doc
  conflicts: ConflictInfo[]
}

interface ConflictInfo {
  start?: LineHandle
  middle?: LineHandle
  end?: LineHandle
}

const ACCEPT_CHANGE_TYPE = {
  BOTH: 'both',
  INCOMING: 'incoming',
  CURRENT: 'current',
} as const

type AcceptChangeType = (typeof ACCEPT_CHANGE_TYPE)[keyof typeof ACCEPT_CHANGE_TYPE]

const CURRENT_CHANGE_REGEX = /^<<<<<<</
const INCOMING_CHANGE_REGEX = /^>>>>>>>/
const SEPARATOR_CHANGE_REGEX = /^=======/
const ANY_MARKER_REGEX = /^(<<<<<<<|=======|>>>>>>>)/

function persistenceKeyFor(filename: string): HTMLElement {
  const persistenceForm = document.querySelector<HTMLFormElement>('.js-resolve-conflicts-form')!
  return persistenceForm.elements.namedItem(filename) as HTMLElement
}

const fileDataCache: WeakMap<HTMLElement, FileMergeData> = new WeakMap()

function getFileData(filename: string): FileMergeData | undefined {
  const persistenceField = persistenceKeyFor(filename)
  return fileDataCache.get(persistenceField)
}

function setFileData(filename: string, data: FileMergeData): void {
  fileDataCache.set(persistenceKeyFor(filename), data)
}

async function setupResolverFor(elem: HTMLAnchorElement) {
  const editorElement = document.querySelector('.js-code-editor')

  if (!(editorElement instanceof HTMLElement)) {
    return
  }

  const editor = await getAsyncCodeEditor(editorElement)

  function onChange() {
    const filename = elem.getAttribute('data-filename') || ''
    const fieldName = `files[${filename}]`

    const data = getFileData(fieldName)

    if (data == null) {
      throw new Error(`Expected file data to be loaded and was not`)
    }

    findAndMarkConflicts(data)
  }

  editor.off('change', onChange)
  editor.on('change', onChange)

  const resolver = editorElement.closest('.js-conflict-resolver')

  if (resolver == null) {
    return
  }

  resolver.classList.add('loading')

  editor.loading(async () => {
    const filename = elem.getAttribute('data-filename')
    if (!filename) {
      return
    }

    const fieldName = `files[${filename}]`
    const resolveFileForm = document.querySelector<HTMLFormElement>('.js-resolve-file-form')!
    ;(resolveFileForm.elements.namedItem('filename') as HTMLInputElement).value = fieldName
    resolveFileForm.querySelector<HTMLElement>('.js-filename')!.textContent = decodeURIComponent(filename)
    resolveFileForm.classList.toggle('is-resolved', elem.classList.contains('resolved'))

    for (const el of elem.parentNode!.children) {
      el.classList.toggle('selected', el === elem)
    }

    let contents = getFileData(fieldName)

    if (!contents) {
      const response = await fetch(elem.href, {
        headers: {
          ...getBaseFetchHeaders(),
          Accept: 'application/json',
        },
      })
      if (!response.ok) {
        const responseError = new Error()
        const statusText = response.statusText ? ` ${response.statusText}` : ''
        responseError.message = `HTTP ${response.status}${statusText}`
        throw responseError
      }
      const rawData = await response.json()

      contents = {
        document: CodeMirror.Doc(rawData.conflicted_file.data, rawData.conflicted_file.codemirror_mime_type),
        headDocument: CodeMirror.Doc(rawData.head.data, rawData.head.codemirror_mime_type),
        baseDocument: CodeMirror.Doc(rawData.base.data, rawData.base.codemirror_mime_type),
        conflicts: [],
      }

      findAndMarkConflicts(contents)

      setFileData(fieldName, contents)
    }

    resolver.classList.remove('loading')

    editor.setDocument(contents.document)

    document.querySelector<HTMLElement>('.js-code-editor .js-conflict-count')!.textContent = new Intl.NumberFormat(
      'en-US',
    ).format(contents.conflicts.length)
    const label = document.querySelector<HTMLElement>('.js-code-editor .js-conflict-label')!
    label.textContent = label.getAttribute(
      contents.conflicts.length === 1 ? 'data-singular-string' : 'data-plural-string',
    )

    const hintButton = document.querySelector<HTMLElement>('.js-conflict-keyboard-hint-button')

    if (!hintButton) {
      goToConflict(true)
    } else {
      goToConflict(true, false)
      hintButton.focus()
    }
  })
}

function acceptBranch(editor: Editor, branch: AcceptChangeType, line: number): void {
  let shouldBeDeleted = false
  const linesToRemove: number[] = []

  editor.eachLine(line, Number.POSITIVE_INFINITY, lineHandle => {
    const currentLine = lineHandle.text
    if (branch === ACCEPT_CHANGE_TYPE.BOTH) {
      if (ANY_MARKER_REGEX.test(currentLine)) {
        linesToRemove.push(editor.getLineNumber(lineHandle)!)
      }
    } else {
      // When accepting incoming change, current change is marked for deletion.
      // When accepting current change, incoming change is marked for deletion.
      if (CURRENT_CHANGE_REGEX.test(currentLine)) {
        shouldBeDeleted = branch === ACCEPT_CHANGE_TYPE.INCOMING
      } else if (SEPARATOR_CHANGE_REGEX.test(currentLine)) {
        shouldBeDeleted = branch === ACCEPT_CHANGE_TYPE.CURRENT
      }

      if (shouldBeDeleted || ANY_MARKER_REGEX.test(currentLine)) {
        linesToRemove.push(editor.getLineNumber(lineHandle)!)
      }
    }
    return INCOMING_CHANGE_REGEX.test(currentLine)
  })

  const ranges = linesToRemove.map(lineToRemove => ({
    anchor: {line: lineToRemove, ch: 0},
    head: {line: lineToRemove, ch: 0},
  }))

  const codeMirrorEditor = editor.getEditor()!

  // Place focus on the start of the accepted changes.
  const firstLine = linesToRemove[0]
  if (firstLine !== null && firstLine !== undefined) {
    codeMirrorEditor.setSelections(ranges)
    codeMirrorEditor.execCommand('deleteLine')
    codeMirrorEditor.setCursor(firstLine, 0)
    setTimeout(() => codeMirrorEditor.focus(), 0)
  }
}

function getStartingLineNumber(lineChild: Element): number {
  return (
    Number(
      lineChild
        .closest('.CodeMirror-gutter-wrapper, .CodeMirror-linewidget')!
        .parentElement!.querySelector('.CodeMirror-linenumber')!.textContent,
    ) - 1
  )
}

// Enables addition of ' (Incoming change)' and  ' (Current change)' pseudoelements
function addConflictMarkerClasses(editor: Editor, conflict: ConflictInfo): void {
  if (conflict.start) {
    const startLine = editor.getLineNumber(conflict.start)!
    editor.addLineClass(startLine, 'text', 'conflict-start-line')
  }

  if (conflict.end) {
    const endLine = editor.getLineNumber(conflict.end)!
    editor.addLineClass(endLine, 'text', 'conflict-end-line')
  }
}

function createLineWidget(editor: Editor) {
  const widget = document.createElement('div')
  const createAcceptButton = (type: AcceptChangeType, cme: Editor) => {
    const button = document.createElement('button')
    button.className = 'btn-link text-bold'
    // eslint-disable-next-line i18n-text/no-en
    button.textContent = `Accept ${type} change${type === 'both' ? 's' : ''}`
    button.addEventListener('click', ({target}) => {
      acceptBranch(cme, type, getStartingLineNumber(target as Element))

      // Need to announce on delay to mitigate conflict with focus event which can prevent announcements from happening.
      setTimeout(() => {
        // eslint-disable-next-line i18n-text/no-en
        forceAnnouncementToScreenReaders(`Successfully applied ${type} change${type === 'both' ? 's' : ''}.`)
      }, 50)
    })
    return button
  }

  widget.append(
    createAcceptButton('current', editor),
    ' | ',
    createAcceptButton('incoming', editor),
    ' | ',
    createAcceptButton('both', editor),
  )
  return widget
}

function findAndMarkConflicts(contents: {document: Doc; conflicts: unknown[]}) {
  let currentConflict: ConflictInfo = {}
  contents.conflicts = []
  const editor = contents.document as Editor
  contents.document.eachLine(lineHandle => {
    if (!lineHandle) {
      return
    }

    editor.setGutterMarker(lineHandle, 'merge-gutter', null)
    editor.removeLineClass(lineHandle, 'text', 'conflict-start-line')
    editor.removeLineClass(lineHandle, 'text', 'conflict-end-line')
    if (lineHandle?.widgets) {
      for (const widget of lineHandle.widgets.slice()) {
        editor.removeLineWidget(widget)
      }
    }

    if (!currentConflict.start && CURRENT_CHANGE_REGEX.test(lineHandle.text)) {
      currentConflict.start = lineHandle
    } else if (currentConflict.start && SEPARATOR_CHANGE_REGEX.test(lineHandle.text)) {
      currentConflict.middle = lineHandle
    } else if (currentConflict.start && INCOMING_CHANGE_REGEX.test(lineHandle.text)) {
      currentConflict.end = lineHandle
    }

    if (currentConflict.start) {
      let selector = '.js-line'

      if (currentConflict.start === lineHandle) {
        selector = '.js-start'
      } else if (currentConflict.middle === lineHandle) {
        selector = '.js-middle'
      } else if (currentConflict.end === lineHandle) {
        selector = '.js-end'
      }

      const element = document.querySelector<HTMLElement>(`.js-conflict-gutters ${selector}`)!.cloneNode(true)

      editor.setGutterMarker(lineHandle, 'merge-gutter', element as HTMLElement)
    }

    if (currentConflict.end) {
      contents.conflicts.push(currentConflict)
      currentConflict = {}
    }
  })

  const conflicts = contents.conflicts as ConflictInfo[]
  for (const conflict of conflicts) {
    const hasWidget = (conflict.start?.widgets?.length ?? 0) > 0
    if (conflict.start && conflict.middle && conflict.end && !hasWidget) {
      const widget = createLineWidget(editor)

      editor.addLineWidget(conflict.start, widget, {
        above: true,
        noHScroll: true,
      })
      addConflictMarkerClasses(editor, conflict)
      editor.setCursor(editor.getCursor())
    }
  }
}

function goToConflict(forward: boolean, focus: boolean = true) {
  const resolveFileForm = document.querySelector<HTMLFormElement>('.js-resolve-file-form')!
  const fileDataKey = (resolveFileForm.elements.namedItem('filename') as HTMLInputElement).value

  const fileData = getFileData(fileDataKey)

  if (fileData == null) {
    throw new Error(`Expected file data to be loaded and was not`)
  }

  const currentLine = fileData.document.getCursor().line

  const conflicts = fileData.conflicts

  let newLineNumber = null

  for (let i = forward ? 0 : conflicts.length - 1; forward ? i < conflicts.length : i >= 0; forward ? i++ : i--) {
    const conflictMiddle = conflicts[i]!.middle

    if (conflictMiddle == null) {
      continue
    }

    const lineNumber = fileData.document.getLineNumber(conflictMiddle)!

    if ((forward && lineNumber > currentLine) || (!forward && lineNumber < currentLine)) {
      newLineNumber = lineNumber
      break
    }
  }

  if (newLineNumber != null) {
    fileData.document.setCursor(newLineNumber, 0)
    fileData.document.getEditor()?.scrollIntoView({line: newLineNumber, ch: 0}, 100)
  }

  if (focus) {
    fileData.document.getEditor()!.focus()
  }
}

on('click', 'a.js-conflicted-file', function (event) {
  const currentTarget = event.currentTarget as HTMLAnchorElement

  event.preventDefault()
  setupResolverFor(currentTarget)
})

on('click', '.js-prev-conflict', function (e) {
  goToConflict(false)
  e.preventDefault()
})

on('click', '.js-next-conflict', function (e) {
  goToConflict(true)
  e.preventDefault()
})

on('submit', 'form.js-resolve-file-form', function (event) {
  const form = event.currentTarget as HTMLFormElement

  event.preventDefault()

  if (form.querySelector<HTMLElement>('button.js-mark-resolved')!.classList.contains('disabled')) {
    return
  }

  const currentConflict = document.querySelector('.js-conflicted-file.selected')
  if (currentConflict) {
    currentConflict.classList.add('resolved')
    const filename = currentConflict.getAttribute('data-filename')
    let displayName = 'File'
    if (filename) {
      try {
        displayName = decodeURIComponent(filename)
      } catch {
        // Ignore decode errors, use default display name
      }
    }
    forceAnnouncementToScreenReaders(`${displayName} marked as resolved`)
  }

  const resolvedCount = document.querySelectorAll('.js-conflicted-file.resolved').length
  const conflictCount = document.querySelectorAll('.js-conflicted-file').length

  if (resolvedCount === conflictCount) {
    const completeElement = document.querySelector('.js-resolve-conflicts-complete')
    if (completeElement) {
      /* eslint-disable-next-line github/no-d-none */
      completeElement.classList.remove('d-none')
    }

    const resolveForm = document.querySelector('.js-resolve-file-form')
    if (resolveForm) {
      resolveForm.classList.add('is-resolved')
    }
  }

  // If there's another conflict after this one, automatically go to it
  let nextConflict = currentConflict

  if (nextConflict != null) {
    do {
      nextConflict = nextConflict.nextElementSibling
    } while (nextConflict && !nextConflict.classList.contains('js-conflicted-file'))
  }

  if (nextConflict instanceof HTMLAnchorElement) {
    return setupResolverFor(nextConflict)
  }
})

on('submit', 'form.js-resolve-conflicts-form', async function (event) {
  const form = event.currentTarget as HTMLFormElement

  event.preventDefault()

  await verifySsoSession()

  //build up the form data to submit
  const formData = new FormData(form)
  const files = form.querySelectorAll<HTMLInputElement>('input.js-file')
  for (const file of files) {
    const data = getFileData(file.name)

    if (data == null) {
      throw new Error(`Expected file data to be loaded and was not`)
    }

    formData.append(file.name, data.document.getValue())
  }

  //submit
  const response = await fetch(form.action, {
    method: 'POST',
    body: formData,
    headers: {
      Accept: 'application/json',
      ...getBaseFetchHeaders(),
    },
  })
  if (!response.ok) {
    const responseError = new Error()
    const statusText = response.statusText ? ` ${response.statusText}` : ''
    responseError.message = `HTTP ${response.status}${statusText}`
    throw responseError
  }

  const resp = await response.json()
  let rulesViolationError = false

  try {
    if (resp.error) {
      // in this case, we knew before we queued the job that another push happened
      throw new Error(resp.error)
    }

    let url = null
    if (resp.job) {
      url = resp.job.url
    } else {
      url = resp.orchestration.url
    }

    const jobStatusResponse = await fetchPoll(url, undefined, undefined, [200, 500])
    if (!jobStatusResponse.ok) {
      const jobStatus = await jobStatusResponse.json()

      // HACK: handle job failures due to repository rule violations
      if (jobStatus?.job?.error_message && /^repository rule violations/i.test(jobStatus.job.error_message)) {
        rulesViolationError = true
      }

      // re-throw error that is typically thrown by fetchPoll
      throw new Error(`HTTP ${response.status} ${response.statusText || ''}`)
    }

    const jobStatus = await jobStatusResponse.json()
    if (jobStatus.orchestration?.error_message) {
      if (/^repository rule violations/i.test(jobStatus.orchestration.error_message)) {
        rulesViolationError = true
      }
      throw new Error(`HTTP ${jobStatusResponse.status} ${jobStatusResponse.statusText || ''}`)
    }

    const editorElement = document.querySelector<HTMLElement>('.js-code-editor')!

    const editor = await getAsyncCodeEditor(editorElement)

    editor.clearConfirmUnloadMessage()

    window.location.pathname += '/..'
  } catch {
    // show the error flash if the job failed
    const completeElement = document.querySelector('.js-resolve-conflicts-complete')

    if (completeElement) {
      /* eslint-disable-next-line github/no-d-none */
      completeElement.classList.toggle('d-none')
    }

    if (rulesViolationError) {
      const failedElement = document.querySelector('.js-resolve-conflicts-repository-rule-violations')
      if (failedElement) {
        /* eslint-disable-next-line github/no-d-none */
        failedElement.classList.toggle('d-none')
        return
      }
    }

    const failedElement = document.querySelector('.js-resolve-conflicts-failed')
    if (failedElement) {
      /* eslint-disable-next-line github/no-d-none */
      failedElement.classList.toggle('d-none')
    }
  }
})

on('change', '.js-conflict-resolver .js-code-editor', async function (event) {
  const currentTarget = event.currentTarget as HTMLElement

  const editor = await getAsyncCodeEditor(currentTarget)

  const resolveFileForm = document.querySelector<HTMLFormElement>('.js-resolve-file-form')!
  const fileDataKey = (resolveFileForm.elements.namedItem('filename') as HTMLInputElement).value

  const data = getFileData(fileDataKey)

  if (data == null) {
    throw new Error(`Expected file data to be loaded and was not`)
  }

  const hasConflicts = data.conflicts.length !== 0 || /^[<>]{7}/m.test(editor.code())

  const button = currentTarget.querySelector<HTMLButtonElement>('button.js-mark-resolved')!
  button.classList.toggle('disabled', hasConflicts)
  button.classList.toggle('tooltipped', hasConflicts)
  if (hasConflicts) {
    button.setAttribute('aria-disabled', 'true')
  } else {
    button.removeAttribute('aria-disabled')
  }
  const label = hasConflicts ? button.getAttribute('data-disabled-label') : ''
  if (label) button.setAttribute('aria-label', label)
})

observe('.js-conflict-list', function (el) {
  const firstConflict = el.querySelector('.js-conflicted-file')
  if (firstConflict instanceof HTMLAnchorElement) {
    setupResolverFor(firstConflict)
  }

  // Ensure page goes full width when PJAXing in
  const timeline = document.querySelector('.new-discussion-timeline')
  if (timeline) {
    timeline.classList.remove('tmp-px-3')
    timeline.classList.add('p-0')
  }
})

on('change', '.js-conflict-resolution-choice-option', function (event) {
  const target = event.target as HTMLInputElement

  const form = target.closest<HTMLFormElement>('.js-resolve-conflicts-form')!
  const button = form.querySelector<HTMLElement>('.js-resolve-conflicts-button')!
  const input = form.querySelector<HTMLElement>('.js-quick-pull-new-branch-name')!

  if (target.value === 'direct') {
    button.textContent = button.getAttribute('data-update-text')!
    button.removeAttribute('data-disable-invalid')
    button.removeAttribute('disabled')
    input.setAttribute('disabled', 'true')
  } else {
    button.textContent = button.getAttribute('data-new-branch-text')!
    button.setAttribute('data-disable-invalid', 'true')
    input.removeAttribute('disabled')
    validate(form)
  }
})
